/* eslint-disable no-unused-vars */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable dot-notation ,radix */
import { useEffect, useState } from 'react';
import { isEmpty } from 'modules/common/helpers/object';
import { Grid, IconButton, Typography } from '@mui/material';
import { Loader } from 'modules/common/components';
import DownloadIcon from '@mui/icons-material/Download';
import NoDataDialog from 'modules/dashboard/components/tab-container/export-visual-data/components/no-data';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'modules/common/auth/selectors';
import * as XLSX from 'xlsx-js-style';
import { selectHotelName } from 'modules/dashboard/selectors';
import { styleXlsxFiles } from './style-xlsx';
import { buildPBIExcelData } from './functions';

/**
 * Export Data component to download visual data in format
 * @param {any} report - PowerBI report
 * @param {boolean} isLoaded - if the visual is loaded or not
 * @param {string} fileName - Name for the xlsx export file
 * @param {string} rawCsv - csv from other components
 * @returns
 */
const ExportToExcel = ({ report, isLoaded, fileName, batchExport = false, setData, metaData }) => {
  //
  const currentUser = useSelector(selectAuthUser);
  const selectedHotel = useSelector(selectHotelName);
  //
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isDialog, setIsDialog] = useState(false);
  //
  const resetInitData = () => {
    setLoading(false);
    setIsDialog(true);
  };
  //
  const exportPbiData = async () => {
    setLoading(true);

    try {
      if (isLoaded && !isEmpty(report)) {
        const { aoa, workbookData } = await buildPBIExcelData({
          report,
          currentUser,
          selectedHotel,
          metaData,
        });

        const worksheet = XLSX.utils.aoa_to_sheet(aoa);

        const updatedSheet = XLSX.read(workbookData + XLSX.utils.sheet_to_csv(worksheet), {
          type: 'string',
        }).Sheets['Sheet1'];

        styleXlsxFiles(updatedSheet, fileName, batchExport, setData);
      } else {
        setMessage('Visual is still loading');
        resetInitData();
      }
    } catch (error) {
      setMessage('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  //
  useEffect(() => {
    if (batchExport) {
      exportPbiData();
    }
  }, [batchExport]);
  //
  return (
    <div>
      <Loader loading={loading}>
        {batchExport ? (
          'Exports Automatically'
        ) : (
          <Grid container direction="row" alignItems="center">
            <Typography
              sx={{
                mr: 1,
              }}
            >
              Export Data
            </Typography>
            <IconButton
              size="small"
              onClick={() => exportPbiData()}
              sx={{
                '& .MuiSvgIcon-root': {
                  width: '1.25rem',
                  height: '1.25rem',
                },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Grid>
        )}
        <NoDataDialog open={isDialog} onClose={() => setIsDialog(false)} message={message} />
      </Loader>
    </div>
  );
};
//
export default ExportToExcel;
